import { devices_config } from '@devices/index';
import { DIMMERS_WITH_MULTIPLE_INPUTS } from 'src/constants';

export interface NormalizeDeviceChannelParams {
	deviceType: string | undefined;
	deviceChannel: number;
	condName: string | undefined;
}

/**
 * Normalizes device channel for condition step so device lookup (createDeviceID) resolves correctly.
 * Centralized logic used by extract_conditions_variables and DeviceStep.svelte.
 * All rules are skipped for Virtual Components/bthome (deviceChannel >= 200).
 */
export const normalizeDeviceChannelForCondition = ({
	deviceType,
	deviceChannel,
	condName,
}: NormalizeDeviceChannelParams): number => {
	let channel = deviceChannel;

	/** Special handling for presence sensors (S4SN-0U61X)
	 * These devices don't have channels - zone info is in the condition name
	 */
	if (deviceType === 'S4SN-0U61X') {
		return 0;
	}

	/** This condition is for the SPDM-001PE01EU, SNDM-00100WW, SPDM-002PE01EU devices
	 * and it is only in condition step where every input has 2 channels. This is not happening in the action step.
	 * When the input is 1, the channels are 0 and 1. When the input is 2, the channels are 2 and 3.
	 * Important: SKIP it for Virtual Components/bthome
	 */
	if (channel < 200 && deviceType && DIMMERS_WITH_MULTIPLE_INPUTS.includes(deviceType)) {
		if (
			!['turn', 'brightness'].includes(condName ?? '') ||
			(['turn', 'brightness'].includes(condName ?? '') && channel >= 2)
		) {
			channel = channel < 2 ? 0 : 1;
		}
	}

	/**
	 * Single-channel relays with multiple inputs (e.g. Pro 1, Pro 1PM): i1/i2 refer to input index,
	 * not output channel. Use channel 0 for device lookup (same as actions) so we resolve the only output.
	 */
	if (
		channel < 200 &&
		(condName === 'input_value' || condName === 'push_type') &&
		devices_config[deviceType]?.modes?.relay?.channels === 1
	) {
		return 0;
	}

	/**
	 * Single-channel lights with multiple inputs (0/1-10V, Pro Dimmer 1PM): input_value/push_type use input index;
	 * device lookup must use output channel 0. Excludes multi-channel (e.g. Pro 2PM). Supports type variants.
	 */
	const lightChannels =
		devices_config[deviceType]?.modes?.light?.channels ??
		devices_config[
			DIMMERS_WITH_MULTIPLE_INPUTS.find(
				(base) => deviceType === base || deviceType?.startsWith(base + '-'),
			)
		]?.modes?.light?.channels;
	const isSingleChannelLightWithTwoInputs =
		deviceType &&
		lightChannels === 1 &&
		DIMMERS_WITH_MULTIPLE_INPUTS.some(
			(base) => deviceType === base || deviceType.startsWith(base + '-'),
		);
	if (
		channel < 200 &&
		(condName === 'input_value' || condName === 'push_type') &&
		isSingleChannelLightWithTwoInputs
	) {
		return 0;
	}

	return channel;
};
